/**
 * Economy — the player's wallet and all money math.
 *
 * Keeps the balance, validates/settles bets, and emits `economy:*` events the
 * HUD renders. Persists the balance to localStorage so a refresh doesn't wipe
 * the player's demo credits.
 */
const STORE_KEY = 'chickenroad.balance.v2';
const START_BALANCE = 10000;

/** 12345.6 → "12 345.60" — space thousands separator, always 2 decimals. */
export function formatMoney(v) {
  const [int, dec] = v.toFixed(2).split('.');
  return `${int.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}.${dec}`;
}

export default class Economy {
  constructor(bus) {
    this.bus = bus;
    this.balance = this._load();
    this.bet = 1;
    this.minBet = 0.1;
    this.maxBet = 200;
    this.staked = 0; // amount locked in the current round
  }

  _load() {
    const raw = Number(localStorage.getItem(STORE_KEY));
    return Number.isFinite(raw) && raw > 0 ? raw : START_BALANCE;
  }

  _save() {
    localStorage.setItem(STORE_KEY, String(Math.round(this.balance * 100) / 100));
  }

  setBet(value) {
    const clamped = Math.max(this.minBet, Math.min(this.maxBet, this.balance, value));
    this.bet = Math.round(clamped * 100) / 100;
    this.bus.emit('economy:bet', { bet: this.bet });
    return this.bet;
  }

  nudgeBet(delta) {
    return this.setBet(this.bet + delta);
  }

  canAfford() {
    return this.balance >= this.bet && this.bet >= this.minBet;
  }

  /** Lock the stake at the start of a round. Returns false if unaffordable. */
  placeBet() {
    if (!this.canAfford()) return false;
    this.staked = this.bet;
    this.balance = round2(this.balance - this.staked);
    this._save();
    this.bus.emit('economy:balance', { balance: this.balance, dir: 'down' });
    return true;
  }

  /** Settle a winning cash-out at `multiplier`. Returns the credited amount. */
  cashOut(multiplier) {
    const payout = round2(this.staked * multiplier);
    this.balance = round2(this.balance + payout);
    this.staked = 0;
    this._save();
    this.bus.emit('economy:balance', { balance: this.balance, dir: 'up' });
    return payout;
  }

  /** Settle a loss (stake already deducted at placeBet — nothing returns). */
  lose() {
    const lost = this.staked;
    this.staked = 0;
    this._save();
    return lost;
  }

  /** Potential payout for a given multiplier at the current stake. */
  potential(multiplier) {
    return round2(this.staked * multiplier);
  }

  /** Top up when the player busts out completely (demo convenience). */
  topUpIfBroke() {
    if (this.balance < this.minBet && this.staked === 0) {
      this.balance = START_BALANCE;
      this._save();
      this.bus.emit('economy:balance', { balance: this.balance, dir: 'up' });
      this.bus.emit('economy:topup', { balance: this.balance });
      return true;
    }
    return false;
  }
}

const round2 = (v) => Math.round(v * 100) / 100;
